import React, {
	useState,
	useRef,
	useEffect,
} from 'react';
import axios from 'axios';
import API from 'Vulture/lib/API.js';
import {
	getSession,
} from 'Vulture/Session.js';
import {
	broncoURL,
} from 'Vulture/ENV.js';
import style from './Chonky/ContentManagerDropdown.module.css';
import CustomIcon from '../Common/CustomIcon/CustomIcon';

/**
 * Dropdown component for managing folder-specific content manager permissions.
 * Handles both directly assigned and inherited permissions.
 */
const FolderContentManagerDropdown = ({
	folder,
	accountUUID,
	options,
	onChange,
	label = 'Folder Permissions',
}) => {
	const [ isOpen, setIsOpen, ] = useState(false);
	const [ selectedValues, setSelectedValues, ] = useState(new Set());
	const [ inheritedValues, setInheritedValues, ] = useState(new Set());
	const [ loading, setLoading, ] = useState(false);
	const dropdownRef = useRef(null);

	// Load folder assignments when folder changes
	useEffect(() => {
		if (folder && folder.id) {
			loadAssignments();
		} else {
			setSelectedValues(new Set());
			setInheritedValues(new Set());
		}
	}, [ folder?.id, ]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ isOpen, ]);

	const loadAssignments = async () => {
		if (!folder || !folder.id || !options) {
			return;
		}

		setLoading(true);

		try {
			// Fetch full folder details with parent_id if not already present
			let fullFolderData = folder;
			
			if (!folder.parent_id && !folder.parentId) {
				try {
					const session = getSession();
					const response = await axios.get(
						`${broncoURL}/api/folder/${folder.id}/details?account_uuid=${accountUUID}`,
						{
							headers: {
								'X-Auth-Session': session.session,
							},
						},
					);
					
					if (response.data && response.data.id) {
						fullFolderData = response.data;
					}
				} catch (err) {
					// If folder details fetch fails, continue with limited folder data
				}
			}
			
			// Fetch folder assignments for each content manager
			const directlyAssignedUsers = new Set();
			const allUserFolders = {};
			
			for (const manager of options) {
				try {
					const userFolders = await API.findRecords(`users/${manager.value}/folder`, {}, null);
					allUserFolders[manager.value] = userFolders;
					
					// Check if this folder is directly assigned
					const hasFolder = userFolders.some((f) => {
						const folderId = typeof f === 'object' ? (f.folder_id || f.id) : f;
						return parseInt(folderId, 10) === parseInt(folder.id, 10);
					});
					
					if (hasFolder) {
						directlyAssignedUsers.add(manager.value);
					}
				} catch (err) {
					// Skip users whose folder data can't be fetched
				}
			}

			// Get inherited managers from parent folders
			const inheritedManagers = await getInheritedManagers(fullFolderData, allUserFolders);

			setSelectedValues(directlyAssignedUsers);
			setInheritedValues(new Set(inheritedManagers));
			setLoading(false);
		} catch (error) {
			console.error('Error loading folder assignments:', error);
			setSelectedValues(new Set());
			setInheritedValues(new Set());
			setLoading(false);
		}
	};

	const getInheritedManagers = async (folderData, allUserFolders) => {
		// If folder has no parent, no inheritance
		if (!folderData.parentId && !folderData.parent_id) {
			return [];
		}

		try {
			// Fetch folder tree to get parent folders
			const session = getSession();
			const response = await axios.get(
				`${broncoURL}/api/folder?account_uuid=${accountUUID}`,
				{
					headers: {
						'X-Auth-Session': session.session,
					},
				},
			);
			const folderTree = response.data;

			// Build parent folder ID chain
			const parentIds = getParentFolderIds(folderData, folderTree);

			if (parentIds.length === 0) {
				return [];
			}

			// Find content managers assigned to any parent folder
			const inheritedUsers = [];
			
			options.forEach((manager) => {
				const userFolders = allUserFolders[manager.value];
				
				if (!userFolders || !Array.isArray(userFolders)) {
					return;
				}
				
				// Check if user has any parent folder assigned
				const hasParentFolder = userFolders.some((f) => {
					const folderId = typeof f === 'object' ? (f.folder_id || f.id) : f;
					return parentIds.includes(parseInt(folderId, 10));
				});
				
				if (hasParentFolder) {
					inheritedUsers.push(manager.value);
				}
			});

			return inheritedUsers;
		} catch (error) {
			console.error('Error getting inherited managers:', error);
			return [];
		}
	};

	const getParentFolderIds = (folderData, folderTree) => {
		const parentIds = [];
		let currentParentId = folderData.parentId || folderData.parent_id;

		// Recursively find all parent folders
		while (currentParentId) {
			parentIds.push(currentParentId);
			
			// Find the parent folder in the tree
			const parentFolder = folderTree.find((f) => {
				return f.id === currentParentId;
			});
			
			if (parentFolder) {
				currentParentId = parentFolder.parent_id;
			} else {
				break;
			}
		}

		return parentIds;
	};

	const handleToggle = async (optionValue) => {
		// Can't toggle inherited permissions
		if (inheritedValues.has(optionValue)) {
			return;
		}

		setLoading(true);

		try {
			const isCurrentlySelected = selectedValues.has(optionValue);
			
			if (isCurrentlySelected) {
				// Remove assignment
				await API.deleteMapRecord('user', optionValue, 'folder', parseInt(folder.id, 10));
				const newSelectedValues = new Set(selectedValues);
				newSelectedValues.delete(optionValue);
				setSelectedValues(newSelectedValues);
			} else {
				// Add assignment
				await API.createMapRecord('user', optionValue, 'folder', {
					folder_id: parseInt(folder.id, 10),
				});
				const newSelectedValues = new Set(selectedValues);
				newSelectedValues.add(optionValue);
				setSelectedValues(newSelectedValues);
			}

			// Notify parent if callback provided
			if (onChange) {
				const allSelected = new Set([ ...selectedValues, ...inheritedValues, ]);
				const selectedOptions = options.filter((opt) => {
					return allSelected.has(opt.value);
				});
				onChange(selectedOptions);
			}

			setLoading(false);
		} catch (error) {
			console.error('Error updating folder assignments:', error);
			alert('Failed to update folder permissions. Please try again.');
			setLoading(false);
		}
	};

	if (!folder) {
		return null;
	}

	const totalSelected = selectedValues.size + inheritedValues.size;
	const displayText = loading ? 'Loading...' :
		totalSelected === 0 ? 'No permissions set' :
		`${folder.name}: ${totalSelected} manager${totalSelected !== 1 ? 's' : ''}`;

	return (
		<div className={style.dropdownContainer} ref={dropdownRef}>
			{label && <label className={style.dropdownLabel}>{label}</label>}

			<div
				className={style.dropdownHeader}
				onClick={() => {
					return setIsOpen(!isOpen);
				}}
			>
				<span className={style.dropdownHeaderText}>
					📁 {displayText}
				</span>
				<CustomIcon
					icon={isOpen ? 'caret-up' : 'caret-down'}
					className={style.dropdownCaret}
				/>
			</div>

			{isOpen && (
				<div className={style.dropdownList}>
					{options.length === 0 ? (
						<div className={style.dropdownEmpty}>No content managers available</div>
					) : (
						options.map((option) => {
							const isDirectlySelected = selectedValues.has(option.value);
							const isInherited = inheritedValues.has(option.value);
							const isSelected = isDirectlySelected || isInherited;
							
							return (
								<div
									key={option.value}
									className={`${style.dropdownItem} ${isSelected ? style.dropdownItemSelected : ''} ${isInherited ? style.dropdownItemDisabled : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										if (!loading) {
											return handleToggle(option.value);
										}
									}}
									title={isInherited ? 'Inherited from parent folder' : ''}
								>
									<div className={style.checkbox}>
										{isSelected ? (
											<CustomIcon 
												icon="check-square" 
												className={`${style.checkboxChecked} ${isInherited ? style.checkboxInherited : ''}`} 
											/>
										) : (
											<CustomIcon 
												icon={[ 'far', 'square', ]} 
												className={style.checkboxUnchecked} 
											/>
										)}
									</div>
									<span className={`${style.dropdownItemLabel} ${isInherited ? style.inheritedLabel : ''}`}>
										{option.label}
										{isInherited && <span className={style.inheritedBadge}> (inherited)</span>}
									</span>
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

export default FolderContentManagerDropdown;
